// ============================================================================
// PsyReportRecords — ПРОТОКОЛЫ МЕТОДИКИ и печать «Заключение по тесту».
//
// Карточки (без таблиц), мобильная вёрстка. Печать — РЕНДЕР ПО ЗАПИСИ на
// сервере (MetadataService.RenderLayout): клиент передаёт только layout_id и
// record_id, поля подставляет сервер. PDF/HTML показываются ВСТРОЕННЫМ
// предпросмотром (без модальных окон), остальные форматы — скачиванием.
//
// Нет печатной формы у методики — действия печати НЕТ (явная подпись), это
// честное состояние конфигурации, а не ошибка интерфейса.
// ============================================================================

import { useEffect, useState } from 'react';
import { Download, Loader2, Printer, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToastStore } from '@/store/toastStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import { base64ToBlob, downloadBytes, isPreviewableMime } from '@/lib/printForm';
import {
  renderPsyPrintForm,
  type PsyProtocolRecord,
  type PsyReportEntry,
} from '@/lib/psyReports';

interface PrintPreview {
  recordId: string;
  label: string;
  fileName: string;
  mimeType: string;
  url: string;
  dataB64: string;
}

export interface PsyReportRecordsProps {
  entry: PsyReportEntry;
  records: PsyProtocolRecord[];
  loading: boolean;
  error: string;
  denied: boolean;
  tableMissing: boolean;
  filtersActive: boolean;
  onRetry: () => void;
}

function recordLabel(record: PsyProtocolRecord): string {
  const date = record.date ? formatValue(record.date, '', 'timestamp') : '';
  return [record.patientName || 'Пациент', date].filter(Boolean).join(' · ');
}

export function PsyReportRecords({
  entry, records, loading, error, denied, tableMissing, filtersActive, onRetry,
}: PsyReportRecordsProps) {
  const showError = useToastStore((s) => s.showError);
  const showSuccess = useToastStore((s) => s.showSuccess);
  const [busyId, setBusyId] = useState('');
  const [printError, setPrintError] = useState('');
  const [preview, setPreview] = useState<PrintPreview | null>(null);

  // Освобождение object URL предпросмотра (и при смене записи, и при закрытии).
  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handlePrint = async (record: PsyProtocolRecord) => {
    if (!entry.printFormId) return;
    setBusyId(record.id);
    setPrintError('');
    try {
      const rendered = await renderPsyPrintForm(entry.printFormId, record.id, entry.printFormType);
      if (isPreviewableMime(rendered.mimeType)) {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        const url = URL.createObjectURL(base64ToBlob(rendered.dataB64, rendered.mimeType));
        setPreview({
          recordId: record.id,
          label: recordLabel(record),
          fileName: rendered.fileName,
          mimeType: rendered.mimeType,
          url,
          dataB64: rendered.dataB64,
        });
        return;
      }
      downloadBytes(rendered.fileName, rendered.mimeType, rendered.dataB64);
      showSuccess(`Печатная форма сформирована: ${rendered.fileName}`);
    } catch (e) {
      logErrorToJournal(e, 'psyReports: печать «Заключение по тесту»');
      const message = friendlyError(e);
      setPrintError(message);
      showError(message);
    } finally {
      setBusyId('');
    }
  };

  const downloadPreview = () => {
    if (!preview) return;
    downloadBytes(preview.fileName, preview.mimeType, preview.dataB64);
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Printer className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Протоколы и печать «Заключение по тесту»</span>
        {!loading && !error && (
          <span className="text-[11px] text-muted-foreground">
            записей: {records.length}{filtersActive ? ' (с отбором)' : ''}
          </span>
        )}
        <Button size="sm" variant="outline" className="h-7 px-2 ml-auto" onClick={onRetry} disabled={loading} title="Обновить протоколы">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      </div>

      {!entry.printFormId && (
        <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground bg-card">
          Печатная форма «Заключение по тесту» для этой методики не найдена — в конфигураторе
          у типа опроса нет печатного макета. Отчёт при этом доступен.
        </div>
      )}

      {loading && (
        <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Загрузка протоколов…
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 px-3 py-2 space-y-1.5">
          <div className="text-sm font-medium text-destructive">Протоколы не загружены</div>
          <div className="text-xs text-muted-foreground break-words">{error}</div>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onRetry}>Повторить</Button>
        </div>
      )}

      {!loading && !error && denied && (
        <div className="rounded-lg border px-3 py-2 text-xs bg-card">
          <div className="text-sm font-medium">Нет доступа к протоколам методики</div>
          <div className="text-muted-foreground">
            Роли не выдано право «Чтение» на этот тип опроса — обратитесь к администратору.
          </div>
        </div>
      )}

      {!loading && !error && !denied && tableMissing && (
        <div className="rounded-lg border px-3 py-2 text-xs bg-card">
          <div className="text-sm font-medium">Таблица протоколов не найдена</div>
          <div className="text-muted-foreground">
            Тип опроса есть в метаданных, но физической таблицы нет — перепримените поставку
            или создайте тип заново в конфигураторе.
          </div>
        </div>
      )}

      {!loading && !error && !denied && !tableMissing && records.length === 0 && (
        <div className="rounded-lg border px-3 py-3 text-xs bg-card">
          <div className="text-sm font-medium">
            {filtersActive ? 'За выбранный период и пациента протоколов нет' : 'Протоколов пока нет'}
          </div>
          <div className="text-muted-foreground mt-1">
            {filtersActive
              ? 'Измените период или пациента — либо сбросьте отбор.'
              : 'Протоколы появляются после прохождения методики или ручного внесения результата.'}
          </div>
        </div>
      )}

      {!loading && !error && !denied && !tableMissing && records.length > 0 && (
        <div className="space-y-2">
          {records.map((record) => (
            <article key={record.id} className="rounded-lg border bg-card px-3 py-2 space-y-1.5">
              <div className="flex items-start gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {record.patientName || 'Пациент не указан'}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {record.date ? formatValue(record.date, '', 'timestamp') : 'дата не указана'}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {record.score !== '' && (
                    <span className="rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
                      балл: {record.score}
                    </span>
                  )}
                  {record.scaledScores !== '' && (
                    <span className="rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground">
                      шкалы: {record.scaledScores}
                    </span>
                  )}
                  {record.passed !== null && (
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${
                      record.passed
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        : 'border-border text-muted-foreground'
                    }`}>
                      {record.passed ? 'порог пройден' : 'порог не пройден'}
                    </span>
                  )}
                </div>
              </div>

              {record.conclusion && (
                <div className="text-xs whitespace-pre-wrap break-words">{record.conclusion}</div>
              )}

              {entry.printFormId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => void handlePrint(record)}
                  disabled={busyId !== ''}
                >
                  {busyId === record.id
                    ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Формирование…</>
                    : <><Printer className="h-3 w-3 mr-1" /> Печать «Заключение по тесту»</>}
                </Button>
              )}
            </article>
          ))}
        </div>
      )}

      {printError && (
        <div className="rounded-lg border border-destructive/40 px-3 py-2 text-xs text-destructive break-words">
          Печать не сформирована: {printError}
        </div>
      )}

      {preview && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate">{preview.label}</div>
              <div className="text-[10px] text-muted-foreground truncate">{preview.fileName}</div>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={downloadPreview}>
              <Download className="h-3 w-3 mr-1" /> Скачать
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={closePreview} title="Закрыть предпросмотр">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <iframe
            src={preview.url}
            title={`Печатная форма: ${preview.label}`}
            className="w-full h-[min(70vh,640px)] bg-white"
          />
        </div>
      )}
    </section>
  );
}
